/**
 * PayrollMatrix — Vue matrice mois × salariés (Vague 2).
 * Édition inline + envoi lot.
 */
import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Send, Save, Settings, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const MONTHS = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const FIELDS = [
  { key: "overtime_hours", label: "HS", w: 60 },
  { key: "absences_days", label: "Abs.", w: 60 },
  { key: "paid_leave_days", label: "CP", w: 60 },
  { key: "sick_leave_days", label: "Arrêt", w: 60 },
  { key: "advance_amount", label: "Avance €", w: 80 },
  { key: "acompte_amount", label: "Acompte €", w: 80 },
  { key: "exceptional_bonus", label: "Prime €", w: 80 },
];

export default function PayrollMatrix() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const period = `${year}-${String(month).padStart(2, "0")}`;

  const load = useCallback(async () => {
    try {
      const r = await api.get("/hr/payroll-variables", { params: { period } });
      setRows(r.data.variables || []);
      setDirty({});
    } catch (e) { toast.error(apiError(e)); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const upd = (eid, key, value) => {
    setDirty(p => ({ ...p, [eid]: { ...(p[eid] || {}), [key]: value } }));
  };
  const val = (r, k) => {
    const d = dirty[r.employee_id];
    if (d && k in d) return d[k];
    return r[k] ?? "";
  };
  const saveOne = async (r) => {
    const patch = dirty[r.employee_id];
    if (!patch) return;
    setSaving(true);
    try {
      const body = { period, ...r, ...patch };
      // Type coerce
      Object.keys(patch).forEach(k => {
        if (body[k] === "" || body[k] === null) body[k] = 0;
        else if (typeof body[k] === "string") body[k] = parseFloat(body[k]) || 0;
      });
      await api.post(`/hr/employees/${r.employee_id}/payroll-variables-v2`, body);
      toast.success(`${r.employee_name} enregistré`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const saveAll = async () => {
    setSaving(true);
    try {
      for (const eid of Object.keys(dirty)) {
        const r = rows.find(x => x.employee_id === eid) || {};
        const body = { period, ...r, ...dirty[eid] };
        Object.keys(dirty[eid]).forEach(k => {
          if (body[k] === "" || body[k] === null) body[k] = 0;
          else if (typeof body[k] === "string") body[k] = parseFloat(body[k]) || 0;
        });
        await api.post(`/hr/employees/${eid}/payroll-variables-v2`, body);
      }
      toast.success("Modifications enregistrées");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const sendBatch = async () => {
    if (Object.keys(dirty).length > 0) { toast.error("Enregistrez d'abord vos modifications"); return; }
    if (!window.confirm(`Envoyer les variables de paie de ${MONTHS[month - 1]} ${year} au cabinet paie ?`)) return;
    setSending(true);
    try {
      const r = await api.post("/hr/payroll-variables/send-batch", null, { params: { period } });
      toast.success(`${r.data.sent}/${r.data.total} envoyés (${r.data.failed} échecs)`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="space-y-6" data-testid="payroll-matrix-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Variables de paie
          </h1>
          <p className="text-sm text-slate-500">
            {MONTHS[month - 1]} {year} · {rows.length} salariés · {dirtyCount} modif{dirtyCount > 1 ? "s" : ""} en attente
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate("/app/hr/settings")}>
            <Settings className="w-4 h-4 mr-1.5" /> Paramètres paie
          </Button>
          {canWrite && dirtyCount > 0 && (
            <Button onClick={saveAll} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="btn-save-all">
              <Save className="w-4 h-4 mr-1.5" /> Enregistrer tout ({dirtyCount})
            </Button>
          )}
          {canWrite && (
            <Button onClick={sendBatch} disabled={sending || dirtyCount > 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-send-batch">
              <Send className="w-4 h-4 mr-1.5" /> Envoyer au cabinet
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-2 gap-2 max-w-md">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger data-testid="matrix-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[year - 1, year, year + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger data-testid="matrix-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {rows.length === 0 ? (
            <div className="p-12 text-center">
              <ReceiptText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucun salarié ce mois-ci.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase min-w-[180px]">Salarié</TableHead>
                {FIELDS.map(f => (
                  <TableHead key={f.key} className="text-xs uppercase text-right" style={{ minWidth: f.w }}>{f.label}</TableHead>
                ))}
                <TableHead className="text-xs uppercase">Commentaire</TableHead>
                <TableHead className="text-xs uppercase text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r, i) => {
                  const isDirty = !!dirty[r.employee_id];
                  return (
                    <TableRow key={r.employee_id} className={`border-b ${isDirty ? "bg-amber-50/60" : "hover:bg-slate-50"}`}
                      data-testid={`matrix-row-${i}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900 text-sm">{r.employee_name}</div>
                        <div className="text-xs text-slate-500">{r.job_title || "—"}</div>
                      </TableCell>
                      {FIELDS.map(f => (
                        <TableCell key={f.key} className="text-right p-1">
                          <Input type="number" step={f.key.includes("hours") || f.key.includes("days") ? "0.5" : "0.01"}
                            value={val(r, f.key)} onChange={(e) => upd(r.employee_id, f.key, e.target.value)}
                            disabled={!canWrite} className="h-8 text-right font-mono text-sm"
                            data-testid={`cell-${r.employee_id}-${f.key}`} />
                        </TableCell>
                      ))}
                      <TableCell className="p-1">
                        <Input value={val(r, "comment")} onChange={(e) => upd(r.employee_id, "comment", e.target.value)}
                          disabled={!canWrite} className="h-8 text-sm" placeholder="—" />
                      </TableCell>
                      <TableCell className="text-right">
                        {canWrite && isDirty && (
                          <Button size="sm" variant="outline" onClick={() => saveOne(r)} disabled={saving}
                            className="h-7 text-xs" data-testid={`save-row-${i}`}>
                            <Save className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
